import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Sparkles, Coins, Package, Calendar, Star, Hourglass, Wrench, Swords, BookOpen, Utensils, Flame, ArrowRight, Dices, Flag, Hammer, Check, RotateCcw } from "lucide-react";

const TABS = [
  { key: "daily",    label: "Daily",    icon: Calendar,  timer: "18h 6m 25s", color: "#e35a5a" },
  { key: "weekly",   label: "Weekly",   icon: Calendar,  timer: "4d 18h 6m",  color: "#9aa3b3" },
  { key: "starting", label: "Starting", icon: Star,      timer: null,         color: "#9aa3b3" },
];

const REWARDS = {
  daily:    { xp: 100, coins: 10, cases: 1, target: 7,  blurb: "Complete 7 of 10 daily missions to unlock the daily bonus chest." },
  weekly:   { xp: 300, coins: 30, cases: 3, target: 7,  blurb: "Complete 7 of 10 weekly missions to claim weekly rewards." },
  starting: { xp: 500, coins: 50, cases: 5, target: 10, blurb: "Complete 10 of 14 starting missions for a one-time onboarding bonus." },
};

const MISSIONS = {
  daily: [
    { icon: Wrench,  text: "Produce items 3 times",            max: 3,    xp: 10 },
    { icon: Flag,    text: "Deal 1273 on country/MU orders",   max: 1270, xp: 10, flame: true },
    { icon: Flag,    text: "Deal 849 for allies or your country", max: 849,  xp: 10, flame: true },
    { icon: Package, text: "Open 1 case",                       max: 1,    xp: 10 },
    { icon: Utensils,text: "Eat 2 food items",                  max: 2,    xp: 10 },
    { icon: BookOpen,text: "Read 1 article",                    max: 1,    xp: 10 },
    { icon: Hammer,  text: "Dismantle 1 item",                  max: 1,    xp: 10 },
    { icon: Swords,  text: "Deal 1698 in battles",              max: 1700, xp: 10, flame: true },
    { icon: Wrench,  text: "Craft 1 item",                      max: 1,    xp: 10 },
    { icon: Swords,  text: "Hit 15 times in battles",           max: 15,   xp: 10 },
  ],
  weekly: [
    { icon: Wrench,  text: "Produce items 30 times",            max: 30,   xp: 30 },
    { icon: Flag,    text: "Deal 8500 on country/MU orders",    max: 8500, xp: 30, flame: true },
    { icon: Package, text: "Open 7 cases",                      max: 7,    xp: 30 },
    { icon: Utensils,text: "Eat 14 food items",                 max: 14,   xp: 30 },
    { icon: BookOpen,text: "Read 7 articles",                   max: 7,    xp: 30 },
    { icon: Hammer,  text: "Dismantle 7 items",                 max: 7,    xp: 30 },
    { icon: Swords,  text: "Deal 12000 in battles",             max: 12000,xp: 30, flame: true },
    { icon: Wrench,  text: "Craft 7 items",                     max: 7,    xp: 30 },
    { icon: Swords,  text: "Hit 100 times in battles",          max: 100,  xp: 30 },
    { icon: Star,    text: "Win 3 battle rounds",               max: 3,    xp: 30 },
  ],
  starting: [
    { icon: BookOpen,text: "Read the codex overview",            max: 1, xp: 50 },
    { icon: Wrench,  text: "Self-work your starter Grain company", max: 1, xp: 50 },
    { icon: Flag,    text: "Set your citizenship",                max: 1, xp: 50 },
    { icon: Package, text: "Open your first case",               max: 1, xp: 50 },
    { icon: Utensils,text: "Eat any food item",                   max: 1, xp: 50 },
    { icon: Hammer,  text: "Craft your first item",               max: 1, xp: 50 },
    { icon: Swords,  text: "Hit in your first battle",            max: 1, xp: 50 },
    { icon: Star,    text: "Join a Military Unit",                max: 1, xp: 50 },
    { icon: Flag,    text: "Place 1 order on your country tab",   max: 1, xp: 50 },
    { icon: Wrench,  text: "Upgrade a company's Storage to Lvl 2", max: 1, xp: 50 },
    { icon: Hammer,  text: "Buy gear from the Equipment Market",  max: 1, xp: 50 },
    { icon: Swords,  text: "Hit 10 times in battles",             max: 10, xp: 50 },
    { icon: Package, text: "Loot any case in battle",             max: 1, xp: 50 },
    { icon: Star,    text: "Reach character level 5",             max: 5, xp: 50 },
  ],
};

const STORAGE_KEY = "territoria_missions_v1";

function loadCompleted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { daily: [], weekly: [], starting: [] };
  } catch (err) {
    console.warn("Missions: failed to read localStorage", err);
    return { daily: [], weekly: [], starting: [] };
  }
}

export default function Missions() {
  const [active, setActive] = useState("daily");
  const [completed, setCompleted] = useState(loadCompleted);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    } catch (err) {
      // Storage may be unavailable (private mode / quota exceeded) — non-fatal.
      console.warn("Missions: failed to persist to localStorage", err);
    }
  }, [completed]);

  const rew = REWARDS[active];
  const list = MISSIONS[active];
  const completedSet = new Set(completed[active] || []);
  const completedCount = completedSet.size;
  const goalMet = completedCount >= rew.target;

  const toggle = (i) => {
    setCompleted(prev => {
      const cur = new Set(prev[active] || []);
      if (cur.has(i)) cur.delete(i); else cur.add(i);
      return { ...prev, [active]: Array.from(cur) };
    });
  };

  const resetTab = () => {
    setCompleted(prev => ({ ...prev, [active]: [] }));
  };

  const tabCount = (k) => (completed[k] || []).length;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="missions-page">
      <Link to="/wiki" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-4" data-testid="missions-back">
        <ChevronLeft className="w-3 h-3" /> Wiki
      </Link>

      <div className="mb-6 md:mb-8">
        <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Daily Operations</span>
        <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">Missions</h1>
        <p className="text-sm md:text-base text-[var(--t-text-dim)] mt-2">Click any mission to mark it complete. Hit the goal in each tab to unlock the chest reward. Your progress saves locally.</p>
        <Link
          to="/wiki/missions-system"
          className="group inline-flex items-center gap-2 mt-4 px-3 py-1.5 border border-[var(--t-border-glow)] bg-[rgba(58,160,255,0.04)] hover:bg-[rgba(58,160,255,0.1)] hover:border-[var(--t-cyan)] transition"
          data-testid="missions-learn-link"
        >
          <BookOpen className="w-3.5 h-3.5 text-[var(--t-cyan)]" />
          <span className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase text-[var(--t-text)] group-hover:text-[var(--t-cyan)]">Learn how missions work</span>
          <ArrowRight className="w-3 h-3 text-[var(--t-blue-glow)] group-hover:translate-x-0.5 transition" />
        </Link>
      </div>

      <div className="panel p-4 md:p-5 mb-8" data-testid="missions-panel">
        <div className="flex items-center justify-between mb-4 gap-3">
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase tracking-[0.04em]">Missions</h2>
          <div className="flex items-center gap-3">
            <span className={`font-mono text-xs tracking-[0.18em] uppercase ${goalMet ? "text-[#39FF14]" : "text-[var(--t-text-muted)]"}`} data-testid="completed-count">
              {completedCount}/{list.length} completed{goalMet && " ✓"}
            </span>
            <button onClick={resetTab} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]" data-testid="reset-tab" title="Reset this tab"><RotateCcw className="w-3 h-3" /></button>
          </div>
        </div>

        {/* TABS */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = active === t.key;
            const count = tabCount(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`relative p-3 md:p-4 text-left transition ${isActive ? "bg-[rgba(227,90,90,0.08)]" : "bg-[rgba(5,12,28,0.6)] hover:bg-[rgba(58,160,255,0.04)]"}`}
                style={{ border: isActive ? `1px solid ${t.color}` : "1px solid var(--t-border)" }}
                data-testid={`tab-${t.key}`}
              >
                {isActive && <>
                  <span className="absolute -top-0.5 -left-0.5 w-3 h-3 border-t-2 border-l-2" style={{ borderColor: t.color }} />
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 border-t-2 border-r-2" style={{ borderColor: t.color }} />
                  <span className="absolute -bottom-0.5 -left-0.5 w-3 h-3 border-b-2 border-l-2" style={{ borderColor: t.color }} />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 border-b-2 border-r-2" style={{ borderColor: t.color }} />
                </>}
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="w-3.5 h-3.5" style={{ color: isActive ? t.color : "var(--t-text-dim)" }} />
                  <span className="font-display text-base md:text-lg uppercase" style={{ color: isActive ? t.color : "var(--t-steel)" }}>{t.label}</span>
                </div>
                <div className="font-display text-lg md:text-xl text-center" style={{ color: isActive ? t.color : "var(--t-steel)" }}>{count}/{MISSIONS[t.key].length}</div>
                {t.timer && <div className="font-mono text-[10px] mt-1 inline-flex items-center gap-1" style={{ color: isActive ? t.color : "var(--t-text-muted)" }}><Hourglass className="w-2.5 h-2.5" /> {t.timer}</div>}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 mb-2 text-sm md:text-base">
          <span className="inline-flex items-center gap-1.5 text-[#d978ff]"><Sparkles className="w-4 h-4" /> <span className="font-display">{rew.xp}</span></span>
          <span className="inline-flex items-center gap-1.5 text-[var(--t-gold)]"><Coins className="w-4 h-4" /> <span className="font-display">{rew.coins}</span></span>
          <span className="inline-flex items-center gap-1.5 text-[var(--t-text)]"><Package className="w-4 h-4" /> <span className="font-display">{rew.cases}</span></span>
        </div>

        <div className="flex items-center gap-2 mb-4" data-testid="progress-bar">
          <Flag className="w-3.5 h-3.5 text-[var(--t-text-dim)]" />
          <span className="font-mono text-xs text-[var(--t-text-dim)] tracking-[0.1em] flex-shrink-0">{Math.min(completedCount, rew.target)}/{rew.target}</span>
          <div className="flex-1 h-1.5 bg-[#0a1428] border border-[var(--t-border)] overflow-hidden">
            <div className="h-full transition-all duration-500" style={{ width: `${Math.min(100, (completedCount / rew.target) * 100)}%`, background: goalMet ? "#39FF14" : "linear-gradient(90deg, var(--t-blue), var(--t-cyan))", boxShadow: goalMet ? "0 0 12px rgba(57,255,20,0.6)" : "none" }} />
          </div>
        </div>

        <div className="space-y-1.5" data-testid="mission-list">
          {list.map((m, i) => {
            const Icon = m.icon;
            const isDone = completedSet.has(i);
            return (
              <button
                key={`${m.text}-${i}`}
                onClick={() => toggle(i)}
                className={`w-full text-left flex items-center gap-3 p-2.5 border transition cursor-pointer ${isDone ? "bg-[rgba(57,255,20,0.06)] border-[#39FF14]" : "bg-[rgba(5,12,28,0.5)] border-[var(--t-border)] hover:border-[var(--t-blue)] hover:bg-[rgba(58,160,255,0.04)]"}`}
                data-testid={`mission-${i}`}
              >
                <span className={`w-6 h-6 flex-shrink-0 inline-flex items-center justify-center border ${isDone ? "border-[#39FF14] bg-[#39FF14] text-[#0a0e1a]" : "border-[var(--t-border-glow)] text-transparent"}`} style={isDone ? { boxShadow: "0 0 10px rgba(57,255,20,0.5)" } : {}}>
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
                <Icon className={`w-5 h-5 flex-shrink-0 ${isDone ? "text-[#39FF14]" : "text-[var(--t-steel-dim)]"}`} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm truncate flex items-center gap-1 ${isDone ? "text-[var(--t-text)] line-through opacity-60" : "text-[var(--t-text)]"}`}>
                    {m.text.split(m.flame ? /(\d+)/ : /__NEVER__/).map((part, idx) =>
                      m.flame && /^\d+$/.test(part)
                        ? <span key={`${m.text}-p-${idx}`} className="inline-flex items-center gap-0.5 text-[#ff9a5a] font-display"><Flame className="w-3 h-3" />{part}</span>
                        : <span key={`${m.text}-p-${idx}`}>{part}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.1em] flex-shrink-0">{isDone ? m.max : 0}/{m.max >= 1000 ? `${(m.max/1000).toFixed(1)}K` : m.max}</span>
                    <div className="flex-1 h-1 bg-[#0a1428]">
                      <div className="h-full transition-all duration-300" style={{ width: isDone ? "100%" : "0%", background: isDone ? "#39FF14" : "var(--t-blue)" }} />
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[#d978ff] flex-shrink-0"><Sparkles className="w-3.5 h-3.5" /><span className="font-display text-sm">{m.xp}</span></span>
                <span onClick={(e) => e.stopPropagation()} className="w-7 h-7 rounded-full inline-flex items-center justify-center bg-[#1a2030] text-[var(--t-steel-dim)] hover:bg-[#2a3245] hover:text-[var(--t-cyan)] transition" title="Reroll (1 free, then 2 coins)"><Dices className="w-3.5 h-3.5" /></span>
                <span className="w-7 h-7 rounded-full inline-flex items-center justify-center bg-[#1a2030] text-[var(--t-steel-dim)] flex-shrink-0"><ArrowRight className="w-3.5 h-3.5" /></span>
              </button>
            );
          })}
        </div>

        {goalMet && (
          <div className="mt-4 p-3 bg-[rgba(57,255,20,0.08)] border border-[#39FF14] flex items-center gap-3" data-testid="goal-met">
            <Check className="w-5 h-5 text-[#39FF14] flex-shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-display text-[#39FF14] uppercase tracking-[0.06em]">Goal reached!</span>
              <span className="text-[var(--t-text-dim)] ml-2">Claim +{rew.xp} XP · {rew.coins} Coins · {rew.cases} Case{rew.cases > 1 ? "s" : ""}.</span>
            </div>
          </div>
        )}
      </div>

      {/* WIKI INFO */}
      <section className="prose-t panel p-5 md:p-7 mb-6" data-testid="missions-info">
        <h2>How Missions Work</h2>
        <p>Missions appear in three tracks. Each track has its own pool, schedule, and reward tier.</p>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Track</th><th>XP / Mission</th><th>Goal</th><th>Completion Reward</th></tr></thead>
            <tbody>
              <tr><td>Daily</td><td>10 XP</td><td>7 of 10</td><td>+100 XP, 10 Coins, 1 Case</td></tr>
              <tr><td>Weekly</td><td>30 XP</td><td>7 of 10</td><td>+300 XP, 30 Coins, 3 Cases</td></tr>
              <tr><td>Starting</td><td>50 XP</td><td>10 of 14</td><td>+500 XP, 50 Coins, 5 Cases total</td></tr>
            </tbody>
          </table>
        </div>
        <h3>Resets</h3>
        <ul>
          <li><strong>Daily</strong> — resets every day at <code>00:00 UTC</code>.</li>
          <li><strong>Weekly</strong> — resets every Monday at <code>00:00 UTC</code>.</li>
          <li><strong>Starting</strong> — one-time onboarding track that never resets.</li>
        </ul>
        <h3>Rerolls</h3>
        <p>Don&apos;t like a mission? Hit the <strong>dice icon</strong> to swap it for a new one.</p>
        <ul>
          <li><strong>1 free reroll</strong> per mission.</li>
          <li>Additional rerolls cost <strong>2 Coins</strong> each.</li>
        </ul>
        <h3>Reroll Strategy</h3>
        <p>Use your free reroll on missions that conflict with your archetype — for example, a Warlord can reroll &quot;Eat 2 food&quot; if they&apos;re hoarding food for a battle. Don&apos;t spend coins on rerolls unless the mission is genuinely impossible (e.g. a 1700-battle-damage mission when you&apos;re Lvl 1 with no gear).</p>
      </section>

      {/* NEW PLAYER EXAMPLE */}
      <section className="panel panel-glow corner-frame p-5 md:p-7" data-testid="new-player-example">
        <div className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#39FF14] uppercase mb-2">// New Citizen Walkthrough</div>
        <h2 className="font-display text-2xl md:text-3xl text-[var(--t-cyan)] uppercase mb-4">A Day-One Mission Run</h2>
        <p className="text-sm md:text-base text-[var(--t-text-dim)] mb-5">Brand new to Territoria? Follow this exact sequence on your first login — you&apos;ll bank ~500 XP and unlock 5 Cases before the day is over.</p>

        <ol className="space-y-3 mb-5">
          {[
            { n: 1, t: "Open the Starting track first", d: "These give 50 XP each and a one-time bonus. Knock out the easy ones (Read 1 article, Eat 2 food, Open 1 case) before lunch." },
            { n: 2, t: "Self-work your free Grain company 3 times", d: "Covers 'Produce items 3 times' and 'Craft 1 item' simultaneously." },
            { n: 3, t: "Buy a Case off the Equipment Market", d: "Open it to clear 'Open 1 case'. Even a Common drop sells back for partial coins." },
            { n: 4, t: "Read any wiki article", d: "Yes, literally — clear it in 5 seconds, claim 10 XP." },
            { n: 5, t: "Hit a defender in any active battle 15 times", d: "Use only the base 10 HP per swing — don't waste ammo. Easy 'Hit 15 times' clear." },
            { n: 6, t: "Reroll the 1700-damage mission", d: "It's a trap for Lvl 1. Use your free reroll for something achievable." },
            { n: 7, t: "Submit your Country MU order if you have one", d: "Even 10 PP counts toward the 'Deal X on country/MU orders' progress." },
          ].map(s => (
            <li key={s.n} className="flex gap-3" data-testid={`step-${s.n}`}>
              <span className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center border border-[#39FF14] text-[#39FF14] font-display text-sm" style={{ boxShadow: "0 0 8px rgba(57,255,20,0.3)" }}>{s.n}</span>
              <div>
                <div className="font-display text-sm md:text-base text-[var(--t-text)] uppercase tracking-[0.02em]">{s.t}</div>
                <div className="text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5 leading-relaxed">{s.d}</div>
              </div>
            </li>
          ))}
        </ol>

        <div className="p-4 bg-[rgba(57,255,20,0.05)] border-l-2 border-[#39FF14]">
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#39FF14] uppercase mb-1">// expected day-one haul</div>
          <p className="text-sm text-[var(--t-text)] flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[#d978ff]"><Sparkles className="w-4 h-4" /> <span className="font-display">~570 XP</span></span>
            <span className="inline-flex items-center gap-1.5 text-[var(--t-gold)]"><Coins className="w-4 h-4" /> <span className="font-display">60 Coins</span></span>
            <span className="inline-flex items-center gap-1.5 text-[var(--t-text)]"><Package className="w-4 h-4" /> <span className="font-display">9 Cases</span></span>
            <span className="text-[var(--t-text-dim)]">= Level 2-3 by end of session.</span>
          </p>
        </div>
      </section>
    </div>
  );
}
